import { z } from "zod";

// Defensief: passthrough() laat onbekende velden door zonder validatiefout.
// State-records bevatten legacy velden uit eerdere cycli die we niet
// willen typechecken in de UI.

export const IdeaScoresSchema = z
  .object({
    P: z.number().optional(),
    M: z.number().optional(),
    T: z.number().optional(),
    Mo: z.number().optional(),
    D: z.number().optional(),
    UA: z.number().optional(),
    FF: z.number().optional(),
    TP: z.number().optional(),
  })
  .passthrough();
export type IdeaScores = z.infer<typeof IdeaScoresSchema>;

export const IdeaSchema = z
  .object({
    id: z.string(),
    title: z.string().optional(),
    thesis: z.string().optional(),
    status: z.string(),
    domain: z.string().optional(),
    cluster: z.string().optional(),
    lens: z.string().optional(),
    scores: IdeaScoresSchema.optional(),
    market_scan_passed: z.boolean().optional(),
    market_scan_score: z.string().optional(),
    selection_state: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .passthrough();
export type Idea = z.infer<typeof IdeaSchema>;

export const CurrentFocusSchema = z
  .object({
    active_cycle: z.union([z.number(), z.string()]).optional(),
    active_shortlist_ids: z.array(z.string()).default([]),
    active_exploration_ids: z.array(z.string()).default([]),
    blocker: z.string().optional(),
    next_action: z.string().optional(),
    stop_condition: z.string().optional(),
    mode: z.string().optional(),
    discovery_mode: z.string().optional(),
  })
  .passthrough();
export type CurrentFocus = z.infer<typeof CurrentFocusSchema>;

export const StudioStateSchema = z
  .object({
    mode: z.string(),
    state_version: z.number().optional(),
    last_updated_at: z.string().optional(),
    current_focus: CurrentFocusSchema,
    pending_actions: z.array(z.string()).default([]),
    ideas: z.array(IdeaSchema).default([]),
  })
  .passthrough();
export type StudioState = z.infer<typeof StudioStateSchema>;

// API response shapes

export const ShortlistEntrySchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  thesis: z.string().optional(),
  cluster: z.string().optional(),
  scores: IdeaScoresSchema.optional(),
  market_scan_passed: z.boolean().optional(),
});
export type ShortlistEntry = z.infer<typeof ShortlistEntrySchema>;

// Captures

export const CaptureTypeSchema = z.enum(["problem", "idea", "improvement"]);
export type CaptureType = z.infer<typeof CaptureTypeSchema>;

export const CaptureInputSchema = z.object({
  type: CaptureTypeSchema,
  text: z.string().min(1).max(2000),
});
export type CaptureInput = z.infer<typeof CaptureInputSchema>;

export const CaptureSchema = z
  .object({
    capture_id: z.string(),
    type: z.string(),
    summary: z.string(),
    tags: z.array(z.string()).default([]),
    status: z.string(),
    source_ref: z.string(),
    captured_at: z.string(),
  })
  .passthrough();
export type Capture = z.infer<typeof CaptureSchema>;

// Captures kunnen partial-success teruggeven: state geschreven maar JSONL niet.
// In dat geval bevat warnings ["audit_log_partial"] zodat de UI weet dat
// retry niet veilig is (auto-retry zou een duplicate maken).
export const CaptureResponseSchema = CaptureSchema.extend({
  warnings: z.array(z.string()).default([]),
});
export type CaptureResponse = z.infer<typeof CaptureResponseSchema>;

// Decisions log entries (decisions.jsonl).
export const DecisionSchema = z
  .object({
    idea_id: z.string(),
    action: z.string(),
    reason: z.string().optional().default(""),
    title: z.string().optional().default(""),
    thesis: z.string().optional().default(""),
    cycle: z.number().nullable().optional(),
    actor: z.string().optional().default("system"),
    decided_at: z.string(),
  })
  .passthrough();
export type Decision = z.infer<typeof DecisionSchema>;

export const SummarySchema = z.object({
  mode: z.string(),
  last_updated_at: z.string().optional(),
  current_focus: CurrentFocusSchema,
  pending_actions: z.array(z.string()),
  shortlist: z.array(ShortlistEntrySchema),
  source: z.object({
    backend: z.enum(["filesystem", "github"]),
    sha: z.string().optional(),
    fetched_at: z.string(),
  }),
});
export type Summary = z.infer<typeof SummarySchema>;
